import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import { useAuthStatus } from '../../utils'
import { Link, useNavigate } from 'react-router-dom'
import { fetchTours, flattenToursResponse } from '../../services/api'
import { IS_PERIOD_CONTRACT_SELECTION_DISABLED } from '../../config/featureFlags'

const Gender = {
	MALE: 'MALE',
	FEMALE: 'FEMALE',
}

/** API/Prisma: startedAt 또는 started_at, TourCategory 또는 tourCategory */
function getTourStartRaw(tour) {
	return tour?.startedAt ?? tour?.started_at
}

function getTourYear(tour) {
	const raw = getTourStartRaw(tour)
	if (raw) {
		const d = new Date(raw)
		if (!Number.isNaN(d.getTime())) return d.getFullYear()
	}
	if (typeof tour?.year === 'number') return tour.year
	return null
}

export const UserSelectionContainer = ({ API_ENDPOINT, nextPath, nextPath_no_checked_tour }) => {
	const navigate = useNavigate()

	const currentDate = new Date()
	let currentYear = currentDate.getFullYear()
	const currentMonth = currentDate.getMonth() + 1
	const currentDay = currentDate.getDate()

	const isEndOfCurrentYear = currentMonth === 12 && currentDay === 31
	if (isEndOfCurrentYear) {
		currentYear += 1
	}

	const isFirstHalfPeriodOptionDisabled = useMemo(() => {
		if (currentDate.getFullYear() < currentYear) {
			return false
		} else if (currentDate.getFullYear() === currentYear) {
			return currentMonth > 6 || (currentMonth === 6 && currentDay > 30)
		}
		return false
	}, [currentDate, currentYear, currentMonth, currentDay])

	const [tourList, setTourList] = useState([])
	const [selectedTourIds, setSelectedTourIds] = useState([])

	const [selectedContractType, setSelectedContractType] = useState(null)
	const [selectedPeriodTypes, setSelectedPeriodTypes] = useState([])

	const [isLoadingData, setIsLoadingData] = useState(true)

	const [auth, onChangeAuthStatus] = useAuthStatus()

	const sendPlayerType = async () => {
		try {
			const response = await axios.get(`${API_ENDPOINT}/users/player/email`, {
				params: {
					userId: auth.user?.id,
				},
			})
			console.log(response)
			return response.data.playerType === 'KLPGA' ? Gender.FEMALE : Gender.MALE
		} catch (error) {
			console.error('플레이어 타입 가져오기 실패:', error)
			return null
		}
	}

	const getPlayerSelectedTour = async () => {
		try {
			const response = await axios.get(`${API_ENDPOINT}/users/selectedTour`, {
				params: {
					userId: auth.user?.id,
				},
			})
			return response.data.userSelectedTour
		} catch (error) {
			console.error('선택목록을 가져오는 중 오류가 발생하였습니다:', error)
			return []
		}
	}

	useEffect(() => {
		const fetchDataAndInitialize = async () => {
			setIsLoadingData(true)
			try {
				let genderForTours = null
				if (auth.user && auth.user.id) {
					genderForTours = await sendPlayerType()
					const tours = await getPlayerSelectedTour()
					setSelectedTourIds(tours)
				}
				const toursPayload = await fetchTours(genderForTours || undefined)
				setTourList(flattenToursResponse(toursPayload))
			} catch (error) {
				console.error('초기 데이터 로딩 중 오류 발생:', error)
			} finally {
				setIsLoadingData(false)
			}
		}
		fetchDataAndInitialize()
		// eslint-disable-next-line react-hooks/exhaustive-deps -- sendPlayerType/getPlayerSelectedTour는 auth·API_ENDPOINT에 묶인 클로저
	}, [auth.user?.id, API_ENDPOINT])

	const filteredTourList = useMemo(() => {
		return tourList.filter((tour) => getTourYear(tour) === currentYear)
	}, [tourList, currentYear])

	const hasAnyTourForCurrentYear = useMemo(() => {
		return tourList.some((tour) => getTourYear(tour) === currentYear)
	}, [tourList, currentYear])

	// 단기/장기: 출전 대회(프로필)와 무관 — 해당 연도에 노출된 대회 일정만 있으면 선택 가능
	const isShortLongContractDisabled = useMemo(() => {
		if (isLoadingData) return true

		return filteredTourList.length === 0
	}, [filteredTourList, isLoadingData])

	useEffect(() => {
		if (!isLoadingData && selectedContractType === null) {
			if (!isShortLongContractDisabled) {
				setSelectedContractType('short')
				setSelectedPeriodTypes([])
			} else if (!IS_PERIOD_CONTRACT_SELECTION_DISABLED) {
				setSelectedContractType('period')
				setSelectedPeriodTypes([])
			}
		}
	}, [isLoadingData, isShortLongContractDisabled, selectedContractType])

	useEffect(() => {
		sessionStorage.setItem('JobPostCurrentYear', currentYear)
		sessionStorage.setItem('JobPostContractType', selectedContractType)
		sessionStorage.setItem('JobPostPeriodType', JSON.stringify(selectedPeriodTypes))
		console.log(currentYear)
		console.log(selectedContractType)
		console.log(selectedPeriodTypes)
	}, [currentYear, selectedContractType, selectedPeriodTypes])

	const handleContractTypeSelect = (type) => {
		if (isLoadingData) return

		if (type === 'period' && IS_PERIOD_CONTRACT_SELECTION_DISABLED) {
			return
		}

		if ((type === 'short' || type === 'long') && isShortLongContractDisabled) {
			return
		}
		setSelectedContractType(type)
		if (type !== 'period') {
			setSelectedPeriodTypes([])
		}
	}

	const handlePeriodTypeSelect = (type) => {
		if (isLoadingData) return

		if (type === 'first-half' && isFirstHalfPeriodOptionDisabled) {
			return
		}

		setSelectedPeriodTypes((prevTypes) => {
			if (prevTypes.includes(type)) {
				return prevTypes.filter((t) => t !== type)
			} else {
				return [...prevTypes, type]
			}
		})
	}

	const isNextButtonDisabled = useMemo(() => {
		if (isLoadingData) return true

		if (!selectedContractType) {
			return true
		}

		if (selectedContractType === 'period') {
			return selectedPeriodTypes.length === 0
		}
		// 단기/장기: 다음 단계에서 대회 선택 — 출전 대회 미설정이어도 진행 가능
		return false
	}, [selectedContractType, selectedPeriodTypes, isLoadingData])

	if (isLoadingData) {
		return <div>데이터를 불러오는 중입니다. 잠시만 기다려 주세요...</div>
	}

	const handleNextClick = (e) => {
		if (isNextButtonDisabled) {
			e.preventDefault()
			return
		}

		if (selectedTourIds.length === 0 && selectedContractType === 'period') {
			e.preventDefault()
			sessionStorage.setItem('JobPostSelectedTours', JSON.stringify([]))
			navigate(nextPath_no_checked_tour)
			return
		}
	}

	return (
		<div className="User-Selection-Container">
			{selectedTourIds.length === 0 && filteredTourList.length !== 0 && (
				<div className="warning-message-container">
					<div className="warning-message">
						<div>
							<svg
								width="14"
								height="14"
								viewBox="0 0 14 14"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									fill-rule="evenodd"
									clip-rule="evenodd"
									d="M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5899 0.5 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.5 10.5899 0.5 7ZM7 4.5C7.27614 4.5 7.5 4.72386 7.5 5V7.5C7.5 7.77614 7.27614 8 7 8C6.72386 8 6.5 7.77614 6.5 7.5V5C6.5 4.72386 6.72386 4.5 7 4.5ZM7 10C7.27614 10 7.5 9.77614 7.5 9.5C7.5 9.22386 7.27614 9 7 9C6.72386 9 6.5 9.22386 6.5 9.5C6.5 9.77614 6.72386 10 7 10Z"
									fill="#FF5D5D"
								/>
							</svg>
							출전 대회 미설정 상태에요. 대회별 계약을 원하시면 출전 대회를
							설정해주세요.
						</div>
					</div>

					<Link to="/" className="selection-button">
						설정하기
					</Link>
				</div>
			)}
			{!hasAnyTourForCurrentYear && (
				<div className="empty-warning-message">
					<svg
						width="14"
						height="14"
						viewBox="0 0 14 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg">
						<path
							fill-rule="evenodd"
							clip-rule="evenodd"
							d="M0.5 7C0.5 3.41015 3.41015 0.5 7 0.5C10.5899 0.5 13.5 3.41015 13.5 7C13.5 10.5899 10.5899 13.5 7 13.5C3.41015 13.5 0.5 10.5899 0.5 7ZM7 4.5C7.27614 4.5 7.5 4.72386 7.5 5V7.5C7.5 7.77614 7.27614 8 7 8C6.72386 8 6.5 7.77614 6.5 7.5V5C6.5 4.72386 6.72386 4.5 7 4.5ZM7 10C7.27614 10 7.5 9.77614 7.5 9.5C7.5 9.22386 7.27614 9 7 9C6.72386 9 6.5 9.22386 6.5 9.5C6.5 9.77614 6.72386 10 7 10Z"
							fill="#656364"
						/>
					</svg>
					<div>
						{IS_PERIOD_CONTRACT_SELECTION_DISABLED
							? '현재 참여 가능한 대회 일정이 공개되지 않았어요. 일정이 열리면 단기·장기 계약으로 공고를 작성할 수 있어요.'
							: '현재 참여 가능한 대회 일정이 공개되지 않았어요. 기간계약만 가능해요!'}
					</div>
				</div>
			)}

			<div className="Contract-Type-Message">계약 유형</div>
			<div className="Contract-Type">
				<div
					className={`short ${selectedContractType === 'short' ? 'contract-type-active' : ''} ${isShortLongContractDisabled ? 'disabled' : ''}`}
					onClick={() => handleContractTypeSelect('short')}>
					<div>단기계약</div>
					<div>1개 대회</div>
				</div>
				<div
					className={`long ${selectedContractType === 'long' ? 'contract-type-active' : ''} ${isShortLongContractDisabled ? 'disabled' : ''}`}
					onClick={() => handleContractTypeSelect('long')}>
					<div>장기 계약</div>
					<div>2개 이상 대회</div>
				</div>
				<div
					className={`period ${selectedContractType === 'period' ? 'contract-type-active' : ''} ${IS_PERIOD_CONTRACT_SELECTION_DISABLED ? 'disabled period-soon' : ''}`}
					onClick={() => handleContractTypeSelect('period')}>
					<div>기간계약</div>
					<div>상반기/하반기</div>
					{IS_PERIOD_CONTRACT_SELECTION_DISABLED ? (
						<span className="contract-soon-chip">오픈예정</span>
					) : null}
				</div>
			</div>
			{selectedContractType === 'period' && (
				<div className="Period-Contract-Type">
					<div className="Period-Type-Message">기간계약 세부</div>
					<div className="Target-Period">
						<div
							className={`first-half ${selectedPeriodTypes.includes('first-half') ? 'period-type-active' : ''} ${isFirstHalfPeriodOptionDisabled ? 'disabled' : ''}`}
							onClick={() => handlePeriodTypeSelect('first-half')}>
							<svg
								className="first-half-period-check-image"
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M0.6875 8C0.6875 3.96142 3.96142 0.6875 8 0.6875C12.0386 0.6875 15.3125 3.96142 15.3125 8C15.3125 12.0386 12.0386 15.3125 8 15.3125C3.96142 15.3125 0.6875 12.0386 0.6875 8ZM10.7077 6.63945C10.8883 6.38665 10.8297 6.03534 10.5769 5.85477C10.3242 5.67421 9.97284 5.73276 9.79227 5.98555L7.36572 9.38273L6.14775 8.16475C5.92808 7.94508 5.57192 7.94508 5.35225 8.16475C5.13258 8.38442 5.13258 8.74058 5.35225 8.96025L7.03975 10.6477C7.15667 10.7647 7.31911 10.8242 7.4839 10.8106C7.64868 10.7969 7.79912 10.7115 7.89523 10.5769L10.7077 6.63945Z"
									fill="currentColor"
								/>
							</svg>
							{currentYear} 상반기
						</div>
						<div
							className={`last-half ${selectedPeriodTypes.includes('last-half') ? 'period-type-active' : ''}`}
							onClick={() => handlePeriodTypeSelect('last-half')}>
							<svg
								className="last-half-period-check-image"
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg">
								<path
									fillRule="evenodd"
									clipRule="evenodd"
									d="M0.6875 8C0.6875 3.96142 3.96142 0.6875 8 0.6875C12.0386 0.6875 15.3125 3.96142 15.3125 8C15.3125 12.0386 12.0386 15.3125 8 15.3125C3.96142 15.3125 0.6875 12.0386 0.6875 8ZM10.7077 6.63945C10.8883 6.38665 10.8297 6.03534 10.5769 5.85477C10.3242 5.67421 9.97284 5.73276 9.79227 5.98555L7.36572 9.38273L6.14775 8.16475C5.92808 7.94508 5.57192 7.94508 5.35225 8.16475C5.13258 8.38442 5.13258 8.74058 5.35225 8.96025L7.03975 10.6477C7.15667 10.7647 7.31911 10.8242 7.4839 10.8106C7.64868 10.7969 7.79912 10.7115 7.89523 10.5769L10.7077 6.63945Z"
									fill="currentColor"
								/>
							</svg>
							{currentYear} 하반기
						</div>
					</div>
				</div>
			)}
			<Link to={nextPath} onClick={handleNextClick}>
				<div className={`next ${isNextButtonDisabled ? 'next-button-disabled' : ''}`}>
					다음
				</div>
			</Link>
		</div>
	)
}
